// @todo enable the following disabled rules see OPENTOK-31136 for more info
/* eslint-disable no-param-reassign, no-void, no-use-before-define, one-var */
/* eslint-disable no-restricted-syntax, no-prototype-builtins */

import { EventEmitter } from 'events';
import clone from 'lodash/clone';
import parseQOS from './parseQOS';
import TruthyChangeCounter from '../TruthyChangeCounter';
import now from '../../../helpers/now';
import createLogger from '../../../helpers/log';

const logging = createLogger('QoS');

class Qos extends EventEmitter {
  _statsCache;
  _isPublisher;
  timeoutId;
  _creationTime;
  offerMessagesReceived;
  prevStats;
  changeCounters;

  constructor(statsCache, isPublisher) {
    super();
    this._statsCache = statsCache;
    this._isPublisher = isPublisher;
    this._creationTime = now();
    this.offerMessagesReceived = 0;
    this.prevStats = { timeStamp: this._creationTime };

    this.changeCounters = {
      videoCodec: new TruthyChangeCounter(),
      audioCodec: new TruthyChangeCounter(),
    };
  }

  startCollecting() {
    if (this.timeoutId) {
      return;
    }

    const collectionLoop = (interval) => {
      const currentTime = now();

      // parseQOS pushes properties onto this object and gives it back
      const blankStats = {
        timeStamp: currentTime,
        duration: Math.round((currentTime - this._creationTime) / 1000),
        period: Math.round((currentTime - this.prevStats.timeStamp) / 1000),
      };

      parseQOS(
        this._statsCache,
        this.prevStats,
        blankStats,
        this._isPublisher,
        (err, parsedStats) => {
          if (err) {
            logging.error('Failed to Parse QOS Stats:', err);
            return;
          }

          this.handleParsedStats(parsedStats);

          // Recalculate the stats
          clearTimeout(this.timeoutId);
          this.timeoutId = setTimeout(() => collectionLoop(Qos.INTERVAL), interval);
        }
      );
    };

    this.timeoutId = setTimeout(() =>
      collectionLoop(Qos.INTERVAL - Qos.INITIAL_INTERVAL), Qos.INITIAL_INTERVAL);
  }

  stopCollecting() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this._peerConnection = null;
  }

  handleOfferMessageReceived() {
    this.offerMessagesReceived += 1;
  }

  handleParsedStats(parsedStats) {
    Object.keys(this.changeCounters).forEach((key) => {
      this.changeCounters[key].onValue(parsedStats[key]);
      // audioCodecChangeCount, videoCodecChangeCount
      parsedStats[`${key}ChangeCount`] = this.changeCounters[key].changeCount;
    });

    parsedStats.offerMessagesReceived = this.offerMessagesReceived;

    const periodicStats = clone(parsedStats);

    // The following stats are reported with cumulative values:
    const periodicDataKeys = ['audioSentBytes', 'audioSentPackets', 'audioSentPacketsLost',
      'videoSentBytes', 'videoSentPackets', 'videoSentPacketsLost', 'videoFramesSent', 'audioRecvBytes',
      'audioRecvPackets', 'audioRecvPacketsLost', 'videoRecvBytes', 'videoRecvPackets',
      'videoRecvPacketsLost', 'videoFramesReceived', 'pauseCount', 'freezeCount',
      'totalPausesDurationMs', 'totalFreezesDurationMs'];

    // This adjusts the QoS values to be periodic, rather than cumulative:
    periodicDataKeys.forEach((dataKey) => {
      periodicStats[dataKey] = this.prevStats?.[dataKey] ?
        parsedStats[dataKey] - this.prevStats[dataKey] :
        parsedStats[dataKey];
    });

    if (periodicStats.videoFramesReceived) {
      periodicStats.videoFrameRateReceived =
        Math.round(periodicStats.videoFramesReceived / parsedStats.period);
    }

    if (periodicStats.simulcastInfo) {
      periodicStats.simulcastInfo.forEach((streamInfo) => {
        const prevStreamInfo = this.prevStats?.simulcastInfo?.find(
          ({ id }) => id === streamInfo.id
        ) || { bytesSent: 0, totalBytesSent: 0 };
        const bits = 8 * (streamInfo.bytesSent - prevStreamInfo.bytesSent);
        const totalBits = 8 * (streamInfo.totalBytesSent - prevStreamInfo.totalBytesSent);
        const bitrate = Math.round(bits / parsedStats.period);
        const totalBitrate = Math.round(totalBits / parsedStats.period);
        if (!periodicStats.simulcastLayersBitrate) {
          periodicStats.simulcastLayersBitrate = bitrate;
          periodicStats.simulcastLayersTotalBitrate = totalBitrate;
        } else {
          periodicStats.simulcastLayersBitrate += `,${bitrate}`;
          periodicStats.simulcastLayersTotalBitrate += `,${totalBitrate}`;
        }
      });
    }
    this.prevStats = parsedStats;

    // We only need framesDecoded and framesEncoded in prevStats for calculating the video framerate
    // in Firefox 96+. So we can safely delete it from periodicStats to avoid putting it in the
    // QOS log.
    delete periodicStats.framesDecoded;
    delete periodicStats.framesEncoded;

    // This is only needed to calculate simulcastBitrate and simulcastTotalBitrate
    delete periodicStats.simulcastInfo;

    this.emit('stats', periodicStats);
  }
}

// Send stats after 1 sec
Qos.INITIAL_INTERVAL = 1000;
// Recalculate the stats every 30 sec
Qos.INTERVAL = 30000;

export default Qos;
